"use strict";
/**
 * Aggregazioni di squadra della tab Panoramica.
 *
 * Qui non ci sono formule nuove: c'è la *politica* con cui si passa dal dato
 * del singolo alla media del gruppo. È la parte che sbagliava, non la matematica.
 *
 * Due regole, valide per tutte le funzioni di questo file:
 *
 *  1. **"Non pervenuto" non è zero.** Chi non ha compilato viene escluso dalla
 *     media, non contato come atleta a terra. Una squadra nuova deve leggere
 *     "nessun dato", non "recupero all'1%".
 *  2. **La media è sugli atleti, non sui documenti.** Chi ha compilato dieci
 *     giorni non pesa dieci volte chi ne ha compilato uno: di ciascuno si
 *     prende il giorno più recente.
 *
 * Ogni funzione restituisce anche su quanti atleti poggia il risultato: senza
 * quel numero "media team" è una parola che può nascondere un campione di uno.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.stressDaRilassatezza = exports.HOOPER_MAX = exports.HOOPER_MIN = exports.recuperoHaDato = void 0;
exports.ultimoRecuperoPerAtleta = ultimoRecuperoPerAtleta;
exports.mediaRecuperoSquadra = mediaRecuperoSquadra;
exports.mediaStressSquadra = mediaStressSquadra;
exports.serieStressGiornaliera = serieStressGiornaliera;
exports.mediaSforzoSquadra = mediaSforzoSquadra;
/** Il documento poggia su almeno una compilazione reale. */
const recuperoHaDato = (r) => Array.isArray(r.availableFactors) && r.availableFactors.length > 0;
exports.recuperoHaDato = recuperoHaDato;
/**
 * Ultimo giorno con dato per ciascun atleta.
 * Esposta perché serve anche alle classifiche (miglior/peggior recupero), che
 * devono ordinare sugli stessi valori della media, non su altri.
 */
function ultimoRecuperoPerAtleta(records) {
    const ultimo = new Map();
    for (const r of records) {
        if (!(0, exports.recuperoHaDato)(r))
            continue;
        const precedente = ultimo.get(r.atleta);
        if (!precedente ||
            new Date(r.giorno).getTime() > new Date(precedente.giorno).getTime()) {
            ultimo.set(r.atleta, r);
        }
    }
    return ultimo;
}
/** Recupero medio della squadra: un valore per atleta, il suo più recente. */
function mediaRecuperoSquadra(records) {
    const ultimo = ultimoRecuperoPerAtleta(records);
    if (ultimo.size === 0)
        return { valore: null, atleti: 0 };
    const valori = [...ultimo.values()].map((r) => r.interpolation);
    return {
        valore: valori.reduce((a, b) => a + b, 0) / valori.length,
        atleti: ultimo.size,
    };
}
/**
 * Estremi della scala Hooper. **Convenzione Leopart: 5 = positivo (meglio),
 * 1 = negativo (peggio)** — tutte le domande sono fraseggiate al positivo
 * (`ReadinessQuestionary`: "Quanto ti senti rilassato oggi?", 1 = Stressato,
 * 5 = Rilassato).
 */
exports.HOOPER_MIN = 1;
exports.HOOPER_MAX = 5;
/**
 * Converte la risposta "quanto sei rilassato" nello stress corrispondente.
 *
 * Il campo si chiama `stressLevel` ma **misura la calma**: 5 = rilassato.
 * Mostrarlo tale e quale sotto l'etichetta "Stress" capovolge il significato —
 * una squadra serena a 3,8/5 veniva presentata come "Stress 70% · Alto", cioè
 * una buona notizia letta come allarme.
 *
 * Nome e numero devono dire la stessa cosa: qui il valore diventa stress vero,
 * sulla stessa scala 1-5 (5 = massimo stress).
 */
const stressDaRilassatezza = (rilassatezza) => exports.HOOPER_MIN + exports.HOOPER_MAX - rilassatezza;
exports.stressDaRilassatezza = stressDaRilassatezza;
const stressValide = (risposte) => risposte.filter((r) => !r.skipped && typeof r.stressLevel === 'number');
/**
 * Stress medio della squadra, sulla scala 1-5 del questionario.
 *
 * Qui la media è sulle risposte e non sugli atleti: lo stress è per sua natura
 * un andamento nel periodo, e schiacciarlo all'ultimo giorno perderebbe proprio
 * l'informazione che serve al preparatore.
 */
function mediaStressSquadra(risposte) {
    const valide = stressValide(risposte);
    if (valide.length === 0)
        return { valore: null, atleti: 0 };
    // Le risposte arrivano come "rilassatezza": si convertono in stress prima di
    // mediare, così il numero dice quello che dice l'etichetta.
    const somma = valide.reduce((acc, r) => acc + (0, exports.stressDaRilassatezza)(r.stressLevel), 0);
    return {
        valore: somma / valide.length,
        atleti: new Set(valide.map((r) => r.atleta)).size,
    };
}
/**
 * Serie giornaliera dello stress per il grafico.
 *
 * I giorni senza compilazioni valgono `null`, non 0: disegnare uno zero
 * significa mostrare un crollo che non è mai avvenuto.
 */
function serieStressGiornaliera(risposte, giorni, formattaGiorno) {
    const valide = stressValide(risposte);
    return giorni.map((date) => {
        const valori = valide.flatMap((r) => formattaGiorno(r.giorno) === date
            ? [(0, exports.stressDaRilassatezza)(r.stressLevel)]
            : []);
        return {
            date,
            mean: valori.length
                ? valori.reduce((a, b) => a + b, 0) / valori.length
                : null,
        };
    });
}
/** Sforzo medio della squadra sulla scala RPE 1-10. */
function mediaSforzoSquadra(risposte) {
    const valide = risposte.filter((r) => !r.skipped && typeof r.value === 'number');
    if (valide.length === 0)
        return { valore: null, atleti: 0 };
    const somma = valide.reduce((acc, r) => acc + r.value, 0);
    return {
        valore: somma / valide.length,
        atleti: new Set(valide.map((r) => r.atleta)).size,
    };
}
